import React, { FC } from 'react';
import type { BillingInvoice } from '@dreamai/types';
import { ReactComponent as DownloadIcon } from '../../icons/download.svg';
import { ReactComponent as ExternalLinkIcon } from '../../icons/external-link.svg';
import { formatDate } from './organisationLabels';

interface OrganisationInvoicesProps {
  /** null while loading or when the list could not be fetched */
  invoices: BillingInvoice[] | null;
}

function formatAmount(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** Date · number · amount · status, with PDF and hosted-page links. */
const OrganisationInvoices: FC<OrganisationInvoicesProps> = ({ invoices }) => {
  if (invoices === null) {
    return <p className='organisation-empty'>Loading invoices…</p>;
  }
  if (invoices.length === 0) {
    return (
      <p
        className='organisation-empty organisation-invoices-empty'
        data-testid='organisation-no-invoices'
      >
        You don&rsquo;t have any invoices yet
      </p>
    );
  }
  return (
    <div className='organisation-table-wrap'>
      <table
        className='organisation-table organisation-invoice-table'
        data-testid='organisation-invoices'
      >
        <thead>
          <tr>
            <th>Date</th>
            <th>Invoice</th>
            <th>Amount</th>
            <th>Status</th>
            <th aria-label='Links' />
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => {
            const paid = inv.paymentStatus === 'PAID';
            return (
              <tr key={inv.id}>
                <td className='organisation-invoice-date'>
                  {formatDate(inv.createdAt)}
                </td>
                <td className='organisation-invoice-number'>
                  {inv.number || inv.id.slice(0, 12)}
                </td>
                <td className='organisation-invoice-amount'>
                  {formatAmount(inv.totalAmount, inv.currency)}
                </td>
                <td>
                  <span
                    className={`organisation-invoice-status ${paid ? 'paid' : 'unpaid'}`}
                  >
                    {paid ? 'Paid' : 'Unpaid'}
                  </span>
                </td>
                <td className='organisation-invoice-actions'>
                  {inv.pdfUrl && (
                    <a
                      href={inv.pdfUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='organisation-invoice-link'
                      title='Download PDF'
                      aria-label={`Download invoice ${inv.number ?? ''}`}
                    >
                      <DownloadIcon aria-hidden='true' />
                    </a>
                  )}
                  {inv.hostedUrl && (
                    <a
                      href={inv.hostedUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='organisation-invoice-link external'
                      title='View invoice'
                      aria-label={`View invoice ${inv.number ?? ''}`}
                    >
                      <ExternalLinkIcon aria-hidden='true' />
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OrganisationInvoices;
